import React, { useContext, useEffect } from 'react';
import {
  checkTokenAndRenderCode,
  isTokenValid,
} from '../utils/common-functions';
import { showWarningMessage } from '../utils/toast-functions';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../reusable-components/AuthContext';
import { CurrentPageContext } from '../reusable-components/CurrentPageContext';
import {
  GenericSearchableSelect,
  OptionElementType,
} from '../reusable-components/GenericSearchableSelect';
import { useGetAllOptions } from '../hooks/get-all-options';
import { useAddNewJobWithZip } from '../hooks/add-new-job-with-zip';
import { TextInput } from '../reusable-components/TextInput';
import { StandardToastContainer } from '../reusable-components/StandardToastContainer';
import { getCurrentUser } from '../services/user-api';
import { CheckboxInput } from '../reusable-components/CheckboxInput';
import { SelectInput } from '../reusable-components/SelectInput';
import { PercentageSelect } from '../reusable-components/PercentageSelect';
import {
  SERVICE_PROVIDER_OPTIONS,
  SERVICE_PROVIDER_OPTIONS_WITHOUT_FONIC_TCHIB,
} from '../utils/service-provider-options';
export const AddJobPage = () => {
  // constants
  const [error, setError] = React.useState<string>('');
  const [company, setCompany] = React.useState<OptionElementType | undefined>(
    undefined,
  );
  const [companiesOptions, setCompaniesOptions] = React.useState<
    OptionElementType[]
  >([]);
  const [userId, setUserId] = React.useState<string>('user-not-set');

  const authContext = useContext(AuthContext);
  const { token } = authContext;
  const {
    fileTypeOptions,
    invoiceTypeOptions,
    serviceProviderOptions,
    networkProviderOptions,
    tariffOptions,
    getAllCompaniesOptions,
  } = useGetAllOptions(token);

  const [fileType, setFileType] = React.useState<OptionElementType>(
    fileTypeOptions[0],
  );
  const [invoiceType, setInvoiceType] = React.useState<OptionElementType>(
    invoiceTypeOptions[0],
  );
  const [tariff, setTariff] = React.useState<OptionElementType>(
    tariffOptions[0],
  );
  const [maxRatingPercentage, setMaxRatingPercentage] =
    React.useState<string>('10');
  const [simOnlyThreshold, setSimOnlyThreshold] = React.useState<string>('10');
  const [baseFeeDiscountRate, setBaseFeeDiscountRate] =
    React.useState<string>('0');

  const [basicFeeServiceProvider, setBasicFeeServiceProvider] =
    React.useState<string>('VODA');

  // boolean flags
  const [omitSIMOnly, setOmitSIMOnly] = React.useState<boolean>(false);
  const [omitSIMOnlyForSmartphones, setOmitSIMOnlyForSmartphones] =
    React.useState<boolean>(false);
  const [addVAT, setAddVAT] = React.useState<boolean>(false);
  const [omitPersistRecords, setOmitPersistRecords] =
    React.useState<boolean>(false);
  const [businessAccount, setBusinessAccount] = React.useState<boolean>(false);
  const [enterpriseDataUsage, setEnterpriseDataUsage] =
    React.useState<boolean>(false);
  const [omitContractWithoutCallDetails, setOmitContractWithoutCallDetails] =
    React.useState<boolean>(false);
  const [withOptions, setWithOptions] = React.useState<boolean>(false);
  const [recoveryMode, setRecoveryMode] = React.useState<boolean>(false);
  const [rateRateBigFour, setRateRateBigFour] = React.useState<boolean>(false);
  const [rateOwnProvider, setRateOwnProvider] = React.useState<boolean>(false);
  const [includeVPN, setIncludeVPN] = React.useState<boolean>(false);

  const [networkProvider, setNetworkProvider] =
    React.useState<OptionElementType>(networkProviderOptions[1]);
  const [serviceProvider, setServiceProvider] =
    React.useState<OptionElementType>(serviceProviderOptions[1]);

  // Collection parameters
  const [serviceProviders, setServiceProviders] =
    React.useState<string>('VODA,E+,O2,TMOB');
  const [targetGroups, setTargetGroups] = React.useState<string>('REG');
  const [ratingAccountingType, setRatingAccountingType] =
    React.useState<string>('OFFER');
  const [connectionCostDiscountRates, setConnectionCostDiscountRates] =
    React.useState<string>('');
  const [appleNumbers, setAppleNumbers] = React.useState<string>('');
  const [selectedPhones, setSelectedPhones] = React.useState<string>('');

  const currentPageContext = useContext(CurrentPageContext);
  const { setCurrentRoute } = currentPageContext;
  const navigate = useNavigate();
  const {
    queryInProgress,
    setQueryInProgress,
    handleFileChange,
    handleSubmit,
    fileName,
  } = useAddNewJobWithZip(
    token,
    setError,
    fileType?.value ?? '',
    invoiceType?.value ?? '',
    networkProvider?.value ?? '',
    serviceProvider?.value ?? '',
    company?.title ?? '',
    company?.value ?? '0',
    userId,
    tariff?.value ?? '',
    ratingAccountingType,
    simOnlyThreshold,
    maxRatingPercentage,
    baseFeeDiscountRate,
    basicFeeServiceProvider,
    omitSIMOnly,
    omitSIMOnlyForSmartphones,
    addVAT,
    omitPersistRecords,
    businessAccount,
    enterpriseDataUsage,
    omitContractWithoutCallDetails,
    withOptions,
    recoveryMode,
    rateRateBigFour,
    rateOwnProvider,
    includeVPN,
    serviceProviders,
    targetGroups,
    connectionCostDiscountRates,
    appleNumbers,
    selectedPhones,
  );

  useEffect(() => {
    setQueryInProgress(true);
    // Start both asynchronous computations
    const companiesPromise = getAllCompaniesOptions();

    // Use Promise.all to wait for both promises to resolve
    Promise.all([companiesPromise, getCurrentUser(token)])
      .then(([companiesOptions, currentUser]) => {
        // Set the state for companies and users options
        setCompaniesOptions(companiesOptions);
        // set the current user id
        setUserId(currentUser?.userId ?? 'user-not-set');
        setQueryInProgress(false);
      })
      .catch((error) => {
        showWarningMessage(
          'Error while searching for users and companies. Maybe the session has expired?',
        );
        console.error('Error while searching for users and companies', error);
        setQueryInProgress(false);
      });
  }, [token]);

  const pageJSX: JSX.Element = (
    <div className="md:container md:mx-auto px-10">
      <StandardToastContainer />
      <form encType="multipart/form-data" onSubmit={handleSubmit}>
        <h1 className="text-2xl font-bold my-4">Add New Job</h1>
        <GenericSearchableSelect
          labelText="File Type:"
          initialValue={fileType}
          options={fileTypeOptions}
          onChange={setFileType}
          custom_test_id="file-type-dropdown"
        />
        <GenericSearchableSelect
          labelText="Invoice Type:"
          initialValue={invoiceType}
          options={invoiceTypeOptions}
          onChange={setInvoiceType}
          custom_test_id="invoice-dropdown"
        />
        <GenericSearchableSelect
          labelText="Network Provider:"
          initialValue={networkProvider}
          options={networkProviderOptions}
          onChange={setNetworkProvider}
          custom_test_id="network-provider-dropdown"
        />
        <GenericSearchableSelect
          labelText="Service Provider:"
          initialValue={serviceProvider}
          options={serviceProviderOptions}
          onChange={setServiceProvider}
          custom_test_id="service-provider-dropdown"
        />
        <GenericSearchableSelect
          labelText="Tariff:"
          initialValue={tariff}
          options={tariffOptions}
          onChange={setTariff}
          custom_test_id="tariff-dropdown"
        />
        <TextInput
          label="Sim Only Threshold (Money in EUR):"
          value={simOnlyThreshold}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSimOnlyThreshold(e.target.value)
          }
          width="w-1/2"
          custom_test_id="sim-only-threshold-input"
        />
        <PercentageSelect
          label="Max Rating Percentage:"
          value={maxRatingPercentage}
          onChange={setMaxRatingPercentage}
          customTestId="max-rating-percentage-dropdown"
        />
        <PercentageSelect
          label="Base Fee Discount Rate:"
          value={baseFeeDiscountRate}
          onChange={setBaseFeeDiscountRate}
          customTestId="base-fee-discount-rate-dropdown"
        />
        <SelectInput
          label="Basic Fee Service Provider:"
          value={basicFeeServiceProvider}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setBasicFeeServiceProvider(e.target.value)
          }
          valuesAndOptions={SERVICE_PROVIDER_OPTIONS}
          width="w-1/2"
          custom_test_id="basic-fee-service-provider-dropdown"
        />
        <div className="w-1/2 grid grid-cols-3">
          <CheckboxInput
            label="Omit SIM Only:"
            value={omitSIMOnly}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOmitSIMOnly(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="Omit SIM Only for Smartphones:"
            value={omitSIMOnlyForSmartphones}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOmitSIMOnlyForSmartphones(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="Add VAT:"
            value={addVAT}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAddVAT(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="Omit Persist Records:"
            value={omitPersistRecords}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOmitPersistRecords(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="Is business account:"
            value={businessAccount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setBusinessAccount(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="Is enterprise data usage:"
            value={enterpriseDataUsage}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEnterpriseDataUsage(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="Omit contract without call details:"
            value={omitContractWithoutCallDetails}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOmitContractWithoutCallDetails(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="With options:"
            value={withOptions}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setWithOptions(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="Recovery mode:"
            value={recoveryMode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setRecoveryMode(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="Rate rate big four:"
            value={rateRateBigFour}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setRateRateBigFour(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="Rate own provider:"
            value={rateOwnProvider}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setRateOwnProvider(e.target.checked)
            }
            width="w-full"
          />
          <CheckboxInput
            label="Include VPN:"
            value={includeVPN}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setIncludeVPN(e.target.checked)
            }
            width="w-full"
          />
        </div>
        <GenericSearchableSelect
          labelText="Customer Company:"
          initialValue={
            company ?? {
              value: '0',
              title: 'Select a company...',
              description: '',
            }
          }
          options={companiesOptions}
          onChange={setCompany}
          custom_test_id="company-dropdown"
        />
        {queryInProgress && (
          <div className="flex justify-start mt-2">
            <div className="loading loading-spinner loading-md"></div>
          </div>
        )}
        <SelectInput
          label="Rate these service providers (CMD+Click to select multiple):"
          multiple={true}
          value={serviceProviders}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setServiceProviders(e.target.value)
          }
          valuesAndOptions={SERVICE_PROVIDER_OPTIONS_WITHOUT_FONIC_TCHIB}
          width="w-1/2"
          custom_test_id="service-providers-dropdown"
          size={6}
        />
        <SelectInput
          label="Target group (CMD+Click to select multiple):"
          multiple={true}
          value={targetGroups}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setTargetGroups(e.target.value)
          }
          valuesAndOptions={[
            { value: 'REG', option: 'Regular' },
            { value: 'STUD', option: 'Students' },
            { value: 'SEN', option: 'Seniors' },
            { value: 'KID', option: 'Kids' },
            { value: 'SBIZ', option: 'Small Business' },
            { value: 'BIZ', option: 'Business' },
            { value: 'COBIZ', option: 'Contract Business' },
            { value: 'SRATE', option: 'Special Rate' },
          ]}
          width="w-1/2"
          custom_test_id="target-group-dropdown"
        />
        <SelectInput
          label="Rating accounting type:"
          value={ratingAccountingType}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setRatingAccountingType(e.target.value)
          }
          valuesAndOptions={[
            { value: 'OFFER', option: 'Offer' },
            { value: 'REGUL', option: 'Regular' },
            { value: 'CONTR', option: 'Controlling' },
          ]}
          width="w-1/2"
          custom_test_id="rating-accounting-type-dropdown"
        />
        <SelectInput
          label="Connection Cost Discount Rates:"
          value={connectionCostDiscountRates}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            setConnectionCostDiscountRates(e.target.value)
          }
          valuesAndOptions={[{ value: '', option: 'Not available yet' }]}
          width="w-1/2"
          custom_test_id="connection-cost-discount-rates-input"
        />
        <TextInput
          label="iPhone Numbers Plan Group (comma separated list of numbers WITHOUT spaces):"
          value={appleNumbers}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setAppleNumbers(e.target.value)
          }
          width="w-1/2"
          custom_test_id="iphone-numbers-input"
        />
        <TextInput
          label="Skip restricted phone numbers (comma separated list of numbers WITHOUT spaces):"
          value={selectedPhones}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSelectedPhones(e.target.value)
          }
          width="w-1/2"
          custom_test_id="selected-phones-dropdown"
        />
        <TextInput
          label="Upload File:"
          fieldType="file"
          custom_test_id="zip-file-upload"
          onChange={handleFileChange}
          value={fileName}
        />
        <div className="max-w-xs py-4 flex justify-center">
          <button
            type="submit"
            className="w-full btn btn-primary w-40"
            data-testid="add-new-job-button"
            disabled={
              queryInProgress ||
              !isTokenValid(token) ||
              !company ||
              userId === 'user-not-set' ||
              !fileType ||
              !invoiceType ||
              !networkProvider ||
              !serviceProvider ||
              !fileName ||
              !tariff
            }
          >
            Start New Job
          </button>
        </div>
      </form>
      <div className="max-w-xs flex justify-center">
        {error && (
          <div data-testid="add-job-page-error-div" className="text-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
  return checkTokenAndRenderCode(token, pageJSX, setCurrentRoute, navigate);
};
